import { useState, useEffect } from "react";
import useCurrency from "../hooks/useCurrency";

const PLANS = [
  {
    id: "pro",
    name: "Pro",
    monthly: 499,
    yearly: 4999,
    color: "blue",
    features: ["50 QR Codes", "Dynamic QR", "Leads & Feedback", "Full Analytics", "WiFi, UPI, Menu QR", "PIN & Coupons", "A/B Testing"],
  },
  {
    id: "agency",
    name: "Agency",
    monthly: 1499,
    yearly: 14999,
    color: "purple",
    features: ["Unlimited QR Codes", "Everything in Pro", "Bulk QR Engine", "Team Members", "Priority Support", "Brand Kit"],
  },
];

export default function UpgradeModal({ user, onClose, onSuccess }) {
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const { currency, setCurrency, format, loading: ratesLoading } = useCurrency();

  const getDisplayPrice = (amountINR) => {
    if (ratesLoading) return `₹${amountINR.toLocaleString()}`;
    return format(amountINR);
  };

  const handleUpgrade = async (planId) => {
    setLoading(planId);
    try {
      const res = await fetch("/api/billing/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planId, billing_cycle: billing }),
      });
      const text = await res.text();
      let sub;
      try { sub = JSON.parse(text); } catch { throw new Error(`Server error: ${text || res.status}`); }
      if (!res.ok) throw new Error(sub.error || "Failed to create subscription");

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        subscription_id: sub.id,
        name: "Digitoly QR",
        description: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan — ${billing}`,
        theme: { color: "#3b82f6" },
        prefill: { email: user?.email },
        handler: async function (response) {
          await fetch("/api/billing/confirm-subscription", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: user.id,
              plan: planId,
              billing_cycle: billing,
              razorpay_subscription_id: sub.id,
              razorpay_payment_id: response.razorpay_payment_id,
            }),
          });
          onSuccess(planId);
          onClose();
        },
        modal: { ondismiss: () => setLoading(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      rzp.on("payment.failed", () => setLoading(null));
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Try again.");
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Upgrade Plan</h2>
            <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Unlock the full power of Digitoly QR</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl transition-colors">✕</button>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
          <div className="flex bg-white/5 rounded-xl p-1 gap-1">
            <button onClick={() => setBilling("monthly")}
              className={`px-6 py-2 rounded-lg text-[11px] font-black uppercase transition-all ${billing === "monthly" ? "bg-white text-black" : "text-gray-500"}`}>
              Monthly
            </button>
            <button onClick={() => setBilling("yearly")}
              className={`px-6 py-2 rounded-lg text-[11px] font-black uppercase transition-all ${billing === "yearly" ? "bg-white text-black" : "text-gray-500"}`}>
              Yearly
              <span className="ml-2 bg-green-500 text-white text-[8px] px-2 py-0.5 rounded-full">SAVE 17%</span>
            </button>
          </div>
        </div>

        {/* Currency Selector */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="text-gray-500 text-[11px] font-black uppercase">Show prices in:</span>
          <select
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-[11px] font-black outline-none cursor-pointer"
          >
            {[
              { code: "INR", label: "🇮🇳 INR" },
              { code: "USD", label: "🇺🇸 USD" },
              { code: "GBP", label: "🇬🇧 GBP" },
              { code: "EUR", label: "🇪🇺 EUR" },
              { code: "AUD", label: "🇦🇺 AUD" },
              { code: "CAD", label: "🇨🇦 CAD" },
              { code: "NZD", label: "🇳🇿 NZD" },
              { code: "CHF", label: "🇨🇭 CHF" },
            ].map(c => <option key={c.code} value={c.code} style={{ background: "#0f172a" }}>{c.label}</option>)}
          </select>
          {currency !== "INR" && (
            <span className="text-gray-600 text-[10px] italic">Charged in INR via Razorpay</span>
          )}
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {PLANS.map((plan) => {
            const priceINR = billing === "monthly" ? plan.monthly : plan.yearly;
            return (
              <div key={plan.id} className={`border rounded-2xl p-6 ${plan.id === "agency" ? "border-purple-500/50 bg-purple-500/5" : "border-blue-500/50 bg-blue-500/5"}`}>
                <h3 className="text-white font-black uppercase tracking-widest text-sm mb-1">{plan.name}</h3>

                {/* Price display */}
                <div className="mb-1">
                  <span className="text-3xl font-black text-white">{getDisplayPrice(priceINR)}</span>
                  <span className="text-gray-500 text-xs ml-1">/{billing === "monthly" ? "mo" : "yr"}</span>
                </div>

                {/* Show INR equivalent when foreign currency selected */}
                {currency !== "INR" && (
                  <p className="text-gray-600 text-[10px] mb-4">≈ ₹{priceINR.toLocaleString()} INR</p>
                )}
                {currency === "INR" && <div className="mb-4" />}

                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="text-gray-400 text-[11px] flex items-center gap-2">
                      <span className="text-green-400">✓</span> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={!!loading}
                  className={`w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all
                    ${plan.id === "agency"
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
                      : "bg-blue-600 text-white hover:bg-blue-500"}
                    ${loading === plan.id ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  {loading === plan.id ? "Processing..." : `Get ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <p className="text-center text-gray-600 text-[10px] uppercase tracking-widest">
            Free plan: 5 QR codes · Static only · No leads or analytics
          </p>
          {currency !== "INR" && (
            <p className="text-center text-gray-700 text-[9px]">
              * Prices are approximate. Exact INR amount charged at checkout.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}